import { useState } from 'react';
import { X, ChevronDown, ChevronUp, MapPin, TrendingUp, Building2 } from 'lucide-react';
import { useSubmarketDetail, getScoreColor, getScoreTier } from '../../hooks/useSubmarketScores';
import { useSubmarketEgiHistory } from '../../hooks/useRentGrowth';
import { ZandiHistoricalChart, ZandiHistoricalChartSkeleton } from '../PropertyView/ZandiHistoricalChart';
import { SectionTitle, GoldBadge } from '../Luxury';
import { Skeleton } from '../ui/Skeleton';

interface SubmarketDetailPanelProps {
  submarketId: number | null;
  onClose: () => void;
}

interface ScoreBarProps {
  label: string;
  weight: string;
  score: number;
}

/**
 * Individual score bar with label, weight, value, and colored progress bar.
 */
function ScoreBar({ label, weight, score }: ScoreBarProps) {
  const color = getScoreColor(score);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="text-ivory/80">{label}</span>
        <div className="flex items-center gap-3">
          <span className="text-stone-400 text-xs">{weight}</span>
          <span className="text-ivory font-mono font-medium w-12 text-right">
            {score.toFixed(1)}
          </span>
        </div>
      </div>
      <div className="h-2 bg-graphite rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${score}%`,
            backgroundColor: color,
          }}
        />
      </div>
    </div>
  );
}

/**
 * Loading skeleton for the detail panel.
 */
function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      {/* Header skeleton */}
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <Skeleton width={180} height={24} />
          <div className="flex gap-2">
            <Skeleton width={60} height={24} />
            <Skeleton width={80} height={24} />
          </div>
        </div>
        <Skeleton width={32} height={32} />
      </div>

      {/* Composite score skeleton */}
      <div className="bg-graphite/50 rounded-lg p-6 text-center">
        <Skeleton width={100} height={16} className="mx-auto" />
        <Skeleton width={80} height={48} className="mx-auto mt-2" />
        <Skeleton width={120} height={14} className="mx-auto mt-2" />
      </div>

      {/* Score bars skeleton */}
      <div className="space-y-4">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="space-y-1.5">
            <div className="flex justify-between">
              <Skeleton width={100} height={16} />
              <Skeleton width={60} height={16} />
            </div>
            <Skeleton height={8} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * SubmarketDetailPanel - Slide-in panel showing detailed submarket scoring.
 *
 * Features:
 * - Header with name, rank badge, tier badge, close button
 * - Large composite score display
 * - 7 individual score bars with weights
 * - Historical performance section
 * - Collapsible neighborhoods and raw data sections
 */
export function SubmarketDetailPanel({
  submarketId,
  onClose,
}: SubmarketDetailPanelProps) {
  const [showNeighborhoods, setShowNeighborhoods] = useState(false);
  const [showRawData, setShowRawData] = useState(false);

  const { data, isLoading, error } = useSubmarketDetail(submarketId);

  // Fetch historical EGI time-series for this submarket
  const egiHistory = useSubmarketEgiHistory(data?.name ?? '');

  // Don't render if no submarket selected
  if (submarketId === null) {
    return null;
  }

  // Error state
  if (error) {
    return (
      <div className="bg-charcoal border border-slate-dark rounded-lg shadow-xl animate-slide-in-right overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-ivory">Error</h3>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-graphite rounded-lg transition-colors"
              aria-label="Close panel"
            >
              <X className="w-5 h-5 text-stone-500" />
            </button>
          </div>
          <p className="text-crimson text-sm">
            Failed to load submarket details. Please try again.
          </p>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading || !data) {
    return (
      <div className="bg-charcoal border border-slate-dark rounded-lg shadow-xl animate-slide-in-right overflow-hidden">
        <div className="p-6">
          <LoadingSkeleton />
        </div>
      </div>
    );
  }

  const { tier, description } = getScoreTier(data.scores.composite);
  const compositeColor = getScoreColor(data.scores.composite);

  return (
    <div className="bg-charcoal border border-slate-dark rounded-lg shadow-xl animate-slide-in-right overflow-hidden flex flex-col h-full">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-ivory flex items-center gap-2">
              <MapPin className="w-5 h-5 text-gold" />
              {data.name}
            </h2>
            <div className="flex items-center gap-2">
              <GoldBadge>Rank #{data.rank}</GoldBadge>
              <span
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider"
                style={{
                  backgroundColor: `${compositeColor}20`,
                  color: compositeColor,
                  border: `1px solid ${compositeColor}40`,
                }}
              >
                {tier}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-graphite rounded-lg transition-colors"
            aria-label="Close panel"
          >
            <X className="w-5 h-5 text-stone-500 hover:text-ivory" />
          </button>
        </div>

        {/* Composite Score Highlight */}
        <div
          className="bg-graphite/50 rounded-lg p-6 text-center border"
          style={{ borderColor: `${compositeColor}30` }}
        >
          <p className="text-stone-500 text-sm uppercase tracking-wider mb-2">
            Composite Score
          </p>
          <p
            className="text-5xl font-bold font-mono"
            style={{ color: compositeColor }}
          >
            {data.scores.composite.toFixed(1)}
          </p>
          <p className="text-stone-500 text-sm mt-2">{description}</p>
        </div>

        {/* EGI History Chart — shown before score breakdown so it's above the fold */}
        {egiHistory.isLoading && <ZandiHistoricalChartSkeleton />}
        {egiHistory.data?.points && egiHistory.data.points.length > 0 && (
          <ZandiHistoricalChart
            data={egiHistory.data.points}
            submarketName={data.name}
          />
        )}

        {/* Historical Performance */}
        {(data.metrics.cagr_5yr !== undefined || data.metrics.rent_growth_1yr !== undefined) && (
          <div className="bg-graphite/30 rounded-lg p-4 space-y-3">
            <h4 className="text-sm font-medium text-gold uppercase tracking-wider">
              Historical Performance
            </h4>
            <div className="grid grid-cols-2 gap-4">
              {data.metrics.cagr_5yr !== undefined && (
                <div>
                  <p className="text-stone-500 text-xs uppercase">5-Year CAGR</p>
                  <p className="text-lg font-mono text-ivory">
                    {(data.metrics.cagr_5yr * 100).toFixed(2)}%
                  </p>
                </div>
              )}
              {data.metrics.rent_growth_1yr !== undefined && (
                <div>
                  <p className="text-stone-500 text-xs uppercase">1-Year Growth</p>
                  <p className="text-lg font-mono text-ivory">
                    {(data.metrics.rent_growth_1yr * 100).toFixed(2)}%
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Individual Score Bars */}
        <div className="space-y-4">
          <SectionTitle icon={<TrendingUp className="w-4 h-4" />} className="text-base mb-4">
            Score Components
          </SectionTitle>
          <ScoreBar label="Rent Gap" weight="25%" score={data.scores.rent_gap} />
          <ScoreBar label="MOBI" weight="15%" score={data.scores.mobi} />
          <ScoreBar label="Momentum" weight="15%" score={data.scores.momentum} />
          <ScoreBar label="Vacancy" weight="15%" score={data.scores.vacancy} />
          <ScoreBar label="Valuation" weight="10%" score={data.scores.valuation} />
          <ScoreBar label="Permit" weight="10%" score={data.scores.permit} />
          <ScoreBar label="Spillover" weight="10%" score={data.scores.spillover} />
        </div>

        {/* Collapsible Neighborhoods */}
        {data.neighborhoods && data.neighborhoods.length > 0 && (
          <div className="border border-slate-dark rounded-lg overflow-hidden">
            <button
              onClick={() => setShowNeighborhoods(!showNeighborhoods)}
              className="w-full flex items-center justify-between p-4 hover:bg-graphite/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-ivory">
                  Neighborhoods ({data.neighborhoods.length})
                </span>
              </div>
              {showNeighborhoods ? (
                <ChevronUp className="w-4 h-4 text-stone-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-stone-500" />
              )}
            </button>
            {showNeighborhoods && (
              <div className="border-t border-slate-dark bg-graphite/20 p-4 space-y-2 max-h-48 overflow-y-auto">
                {data.neighborhoods.map((neighborhood) => (
                  <div
                    key={neighborhood.id}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-ivory/80">{neighborhood.name}</span>
                    <span className="text-stone-500 font-mono">
                      ${neighborhood.egi.toLocaleString()}/unit
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Collapsible Raw Data */}
        <div className="border border-slate-dark rounded-lg overflow-hidden">
          <button
            onClick={() => setShowRawData(!showRawData)}
            className="w-full flex items-center justify-between p-4 hover:bg-graphite/30 transition-colors"
          >
            <span className="text-sm font-medium text-ivory">Raw Data</span>
            {showRawData ? (
              <ChevronUp className="w-4 h-4 text-stone-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-stone-500" />
            )}
          </button>
          {showRawData && (
            <div className="border-t border-slate-dark bg-graphite/20 p-4 space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-stone-500 text-xs uppercase">Current EGI</p>
                  <p className="font-mono text-ivory">
                    ${data.metrics.current_egi?.toLocaleString() ?? 'N/A'}/unit
                  </p>
                </div>
                <div>
                  <p className="text-stone-500 text-xs uppercase">Metro EGI</p>
                  <p className="font-mono text-ivory">
                    ${data.metrics.metro_egi?.toLocaleString() ?? 'N/A'}/unit
                  </p>
                </div>
                <div>
                  <p className="text-stone-500 text-xs uppercase">MOBI Ratio</p>
                  <p className="font-mono text-ivory">
                    {data.metrics.current_egi && data.metrics.metro_egi
                      ? (data.metrics.current_egi / data.metrics.metro_egi).toFixed(2)
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-stone-500 text-xs uppercase">Vacancy Rate</p>
                  <p className="font-mono text-ivory">
                    {data.metrics.vacancy_rate !== undefined
                      ? `${(data.metrics.vacancy_rate * 100).toFixed(1)}%`
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-stone-500 text-xs uppercase">Cap Rate</p>
                  <p className="font-mono text-ivory">
                    {data.metrics.cap_rate !== undefined
                      ? `${(data.metrics.cap_rate * 100).toFixed(2)}%`
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-stone-500 text-xs uppercase">Permits/1000</p>
                  <p className="font-mono text-ivory">
                    {data.metrics.permits_per_1000?.toFixed(1) ?? 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SubmarketDetailPanel;
